import type { RecipeMeResponse } from "@bitacorapro/api-types";
import { Link, useLocation } from "react-router-dom";
import { adminWords } from "../lib/admin/words";
import { useRecipe } from "../lib/api/queries";
import { ADMIN_BASE_PATH, ADMIN_SECTION_NAV, declaredAdminSections } from "../lib/nav";
import { threadWords } from "../lib/postventa/words";
import { Icon } from "./icons";

// Breadcrumb derivation from the current pathname. Each segment maps
// to a human label; unknown segments fall back to the raw slug.
const SEGMENT_LABELS: Record<string, string> = {
  "": "Dashboard",
  tiendas: "Tiendas",
  proyectos: "Proyectos",
  observaciones: "Observaciones",
  archivo: "Archivo",
  consultas: "Consultas",
  avisos: "Avisos",
  analisis: "Análisis con IA",
  ia: "AI Insights",
  equipo: "Equipo",
  enlaces: "Enlaces compartidos",
  configuracion: "Configuración",
  "carga-unidades": "Cargar unidades",
  documentos: "Documentos",
  // ADR-0173 fase 2 — client context layer.
  contexto: "Contexto del cliente",
  fuentes: "Documentos",
  reglas: "Revisión de reglas",
  versiones: "Versiones",
  diff: "Qué cambió",
};

type Crumb = { label: string; to: string };

// Autogestión (plan 2026-10-30 §1.5): «Administración / Resumen» en
// /administracion (la portada del módulo es la sección Resumen, cuando la
// receta la declara) y «Administración / <sección>» en cada sección, con
// los títulos de lib/admin/words. La sección se reconoce por la ruta del
// registro de nav (ADMIN_SECTION_NAV), no por el slug del segmento. Lo que
// cuelgue más abajo de una sección sigue la regla general («Detalle»).
function adminCrumbs(segments: string[], recipe: RecipeMeResponse | undefined): Crumb[] {
  const words = adminWords(recipe);
  const crumbs: Crumb[] = [{ label: words.module, to: ADMIN_BASE_PATH }];
  const section = segments[1];
  if (section === undefined) {
    if (declaredAdminSections(recipe).some((s) => s.section === "resumen")) {
      crumbs.push({ label: words.sections.resumen, to: ADMIN_BASE_PATH });
    }
    return crumbs;
  }
  const sectionPath = `${ADMIN_BASE_PATH}/${section}`;
  const entry = ADMIN_SECTION_NAV.find((s) => s.path === sectionPath);
  crumbs.push({ label: entry ? words.sections[entry.section] : section, to: sectionPath });
  for (let i = 2; i < segments.length; i++) {
    crumbs.push({ label: "Detalle", to: "/" + segments.slice(0, i + 1).join("/") });
  }
  return crumbs;
}

// Each crumb links to the cumulative path prefix up to its segment. Every
// prefix of a Shell route (/proyectos/:id/observaciones/:obsId, /tiendas/:id,
// /proyectos/:id/archivo, …) is itself a route in App.tsx, so the links land.
// The roster is named by threadWords («Contratistas» in every vertical
// since 2026-10-30); while the recipe loads the generic one stands in.
export function deriveCrumbs(pathname: string, recipe?: RecipeMeResponse): Crumb[] {
  if (pathname === "/" || pathname === "") return [{ label: "Dashboard", to: "/" }];
  const segments = pathname.split("/").filter(Boolean);
  if (`/${segments[0]}` === ADMIN_BASE_PATH) return adminCrumbs(segments, recipe);
  return segments.map((seg, i) => {
    const to = "/" + segments.slice(0, i + 1).join("/");
    if (i === 0 && seg === "contratistas") return { label: threadWords(recipe).assigneePlural, to };
    if (SEGMENT_LABELS[seg]) return { label: SEGMENT_LABELS[seg], to };
    // For dynamic segments (e.g. /tiendas/<id>), render a short
    // placeholder until the screen passes the resolved name via a
    // context. v1 just shows "Detalle" — the per-screen heading
    // already names the asset.
    if (i > 0) return { label: "Detalle", to };
    return { label: seg, to };
  });
}

export function Topbar({ onOpenCmdk }: { onOpenCmdk: () => void }) {
  const { pathname } = useLocation();
  const { data: recipe } = useRecipe();
  const crumbs = deriveCrumbs(pathname, recipe);

  return (
    <header className="topbar">
      <nav className="topbar__crumbs">
        {/* La clave lleva el índice: en /administracion las dos migas
            («Administración / Resumen») apuntan a la misma ruta. */}
        {crumbs.map((c, i) => (
          <span key={`${i}:${c.to}`} className="crumb-group">
            {i > 0 && <span className="crumb-sep">/</span>}
            {i === crumbs.length - 1 ? (
              <span className="crumb crumb--current" aria-current="page">
                {c.label}
              </span>
            ) : (
              <Link to={c.to} className="crumb">
                {c.label}
              </Link>
            )}
          </span>
        ))}
      </nav>

      <div className="topbar__spacer" />

      <button type="button" className="topbar__search" onClick={onOpenCmdk}>
        <Icon.Search />
        <span className="topbar__search-text">Buscar tiendas, observaciones, personas…</span>
        <span className="topbar__search-kbd">⌘K</span>
      </button>
    </header>
  );
}
